import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
	Grid,
	Typography,
	Box,
	Chip,
	Autocomplete,
	TextField,
	FormControlLabel,
	Checkbox,
} from '@mui/material';

import Drawer from '../common/Drawer';
import Input from '../common/form/Input';
import SaveCancelButtons from '../common/buttons/SaveCancelButtons';
import Button from '../common/buttons/Button';
import { AppDispatch, RootState } from '../../data/store';
import {
	updateUserGroup,
	deleteUserGroup,
} from '../../data/thunks/userGroupThunk';
import {
	UpdateUserGroupInput,
	GroupMemberOption,
	UserGroup,
} from '../../data/models/userGroup';
import { fetchUsers } from '../../data/thunks/userThunk';
import { fetchCompanyExternalUsers } from '../../data/thunks/companyExternalThunk';
import Dialog, {
	DialogContent,
	DialogTitle,
	DialogActions,
} from '../common/Dialog';

interface EditGroupModalProps {
	open: boolean;
	handleClose: () => void;
	group?: UserGroup;
}

type FormValues = {
	name: string;
	// description: string;
	selectedMembers: GroupMemberOption[];
	alwaysAddToProjects: boolean;
};

export default function EditGroupModal(props: EditGroupModalProps) {
	const { open, handleClose, group } = props;
	const dispatch = useDispatch<AppDispatch>();

	const { company } = useSelector((state: RootState) => state.companyState);
	const { users } = useSelector((state: RootState) => state.companyState);
	const { companyExternalUsers } = useSelector(
		(state: RootState) => state.companyExternalUserState
	);

	const [memberOptions, setMemberOptions] = useState<GroupMemberOption[]>([]);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);

	const defaultValues: FormValues = {
		name: '',
		// description: '',
		selectedMembers: [],
		alwaysAddToProjects: false,
	};

	const { handleSubmit, control, reset, formState } = useForm({
		defaultValues,
		mode: 'onBlur',
	});
	const { errors } = formState;

	// Load users and external users when modal opens
	useEffect(() => {
		if (open && company?.id) {
			// Load users
			if (!users?.initialized) {
				dispatch(
					fetchUsers({
						// companyId: company.id,
						take: 1000, // Get all users for selection
						page: 0,
					})
				);
			}

			// Load external users
			if (!companyExternalUsers?.initialized) {
				dispatch(
					fetchCompanyExternalUsers({
						// companyId: company.id,
						take: 1000, // Get all external users for selection
						page: 0,
					})
				);
			}
		}
	}, [
		open,
		company?.id,
		dispatch,
		users?.initialized,
		companyExternalUsers?.initialized,
	]);

	// Create member options for autocomplete
	useEffect(() => {
		const options: GroupMemberOption[] = [];

		// Add internal users
		if (users?.list) {
			users.list.forEach((user) => {
				options.push({
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
					role: user.role,
					type: 'user',
					display: `${user.firstName} ${user.lastName} (${user.email})`,
				});
			});
		}

		// Add external users
		if (companyExternalUsers?.list) {
			companyExternalUsers.list.forEach((user) => {
				options.push({
					id: user.id,
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
					role: user.role,
					type: 'externalUser',
					display: `${user.firstName} ${user.lastName} (${user.email}) - External`,
				});
			});
		}

		setMemberOptions(options);
	}, [users?.list, companyExternalUsers?.list]);

	// Reset form when group changes
	useEffect(() => {
		if (group && memberOptions.length > 0) {
			const selectedMembers: GroupMemberOption[] = [];

			// Add current group users (IDs only in UserGroup)
			if (group.users) {
				group.users.forEach((userId) => {
					const option = memberOptions.find(
						(opt) => opt.id === userId && opt.type === 'user'
					);
					if (option) {
						selectedMembers.push(option);
					}
				});
			}

			// Add current group external users (IDs only in UserGroup)
			if (group.externalUsers) {
				group.externalUsers.forEach((userId) => {
					const option = memberOptions.find(
						(opt) =>
							opt.id === userId && opt.type === 'externalUser'
					);
					if (option) {
						selectedMembers.push(option);
					}
				});
			}

			reset({
				name: group.name || '',
				// description: group.description || '',
				selectedMembers: selectedMembers,
				alwaysAddToProjects: false, // TODO: This should come from the actual group data
			});
		}
	}, [group, memberOptions, reset]);

	const resetForm = () => {
		reset(defaultValues);
	};

	const handleOnSaveClick = (data: FormValues) => {
		if (!group?.id) return;

		// Separate users and external users
		const userIds: string[] = [];
		const externalUserIds: string[] = [];

		data.selectedMembers.forEach((member) => {
			if (member.type === 'user') {
				userIds.push(member.id);
			} else {
				externalUserIds.push(member.id);
			}
		});

		const updateData: UpdateUserGroupInput = {
			name: data.name,
			// description: data.description || undefined,
			users: userIds,
			externalUsers: externalUserIds,
		};

		dispatch(updateUserGroup(group.id, updateData));
		resetForm();
		handleClose();
	};

	const handleCancelCloseOnClick = () => {
		resetForm();
		handleClose();
	};

	const handleDeleteClick = () => {
		setShowDeleteDialog(true);
	};

	const handleConfirmDelete = () => {
		if (group?.id) {
			dispatch(deleteUserGroup(group.id));
			setShowDeleteDialog(false);
			handleClose();
		}
	};

	if (!open || !group) return <></>;

	return (
		<>
			<Drawer
				open={open}
				handleClose={handleCancelCloseOnClick}
				title="Edit Group"
				width={600}
			>
				<Container>
					<Grid container spacing={3}>
						{/* Group Name */}
						<Grid item xs={12}>
							<Controller
								name="name"
								control={control}
								rules={{ required: 'Group name is required' }}
								render={({ field }) => (
									<Input
										{...field}
										label="Group name"
										required
										fullWidth
										error={!!errors.name}
										helperText={errors.name?.message}
									/>
								)}
							/>
						</Grid>

						{/* Group Members */}
						<Grid item xs={12}>
							<Controller
								name="selectedMembers"
								control={control}
								render={({ field }) => (
									<Box>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ mb: 1 }}
										>
											Group members
										</Typography>
										<Box sx={{ mb: 2 }}>
											{field.value.map((member) => (
												<Chip
													key={member.id}
													label={`${member.firstName} ${member.lastName}`}
													onDelete={() => {
														const newMembers =
															field.value.filter(
																(m) =>
																	m.id !==
																	member.id
															);
														field.onChange(
															newMembers
														);
													}}
													color={
														member.type === 'user'
															? 'primary'
															: 'secondary'
													}
													size="small"
													sx={{ mr: 1, mb: 1 }}
												/>
											))}
										</Box>
										<Autocomplete
											multiple
											options={memberOptions.filter(
												(option) =>
													!field.value.some(
														(selected) =>
															selected.id ===
															option.id
													)
											)}
											getOptionLabel={(option) =>
												option.display
											}
											isOptionEqualToValue={(
												option,
												value
											) => option.id === value.id}
											renderInput={(params) => (
												<TextField
													{...params}
													placeholder="Add more people..."
													variant="outlined"
													fullWidth
												/>
											)}
											renderOption={(props, option) => (
												<li {...props}>
													<Box
														sx={{
															display: 'flex',
															flexDirection:
																'column',
															width: '100%',
														}}
													>
														<Typography variant="body2">
															{option.firstName}{' '}
															{option.lastName}
														</Typography>
														<Typography
															variant="caption"
															color="text.secondary"
														>
															{option.email} •{' '}
															{option.type ===
															'user'
																? 'Internal'
																: 'External'}{' '}
															{option.role &&
																`• ${option.role}`}
														</Typography>
													</Box>
												</li>
											)}
											onChange={(_, newMembers) => {
												const combinedMembers = [
													...field.value,
													...newMembers,
												];
												field.onChange(combinedMembers);
											}}
											value={[]}
											sx={{
												'& .MuiOutlinedInput-root': {
													minHeight: '56px',
												},
											}}
										/>
									</Box>
								)}
							/>
						</Grid>

						{/* Always Add to Projects */}
						<Grid item xs={12}>
							<Controller
								name="alwaysAddToProjects"
								control={control}
								render={({ field }) => (
									<FormControlLabel
										control={
											<Checkbox
												checked={field.value}
												onChange={field.onChange}
												color="primary"
											/>
										}
										label="Always add to projects"
									/>
								)}
							/>
						</Grid>

						{/* Delete Group Button */}
						<Grid item xs={12}>
							<Box sx={{ mt: 2 }}>
								<Button
									variant="text"
									color="error"
									onClick={handleDeleteClick}
								>
									DELETE GROUP
								</Button>
							</Box>
						</Grid>
					</Grid>

					<ButtonContainer>
						<SaveCancelButtons
							handleSave={handleSubmit(handleOnSaveClick)}
							handleCancel={handleCancelCloseOnClick}
						/>
					</ButtonContainer>
				</Container>
			</Drawer>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={showDeleteDialog}
				handleClose={() => setShowDeleteDialog(false)}
			>
				<DialogTitle>Delete Group</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete the group "
						<strong>{group?.name}</strong>"? This action cannot be
						undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setShowDeleteDialog(false)}
						variant="outlined"
					>
						Cancel
					</Button>
					<Button onClick={handleConfirmDelete} color="error">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 16px 0;
`;

const ButtonContainer = styled.div`
	position: absolute;
	bottom: 16px;
	right: 16px;
`;
